import { CalendarPlus, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { download, wrap, eventToV, hotelToV, transportToV, dayToICS, tripToICS, googleUrl } from "@/lib/ics";
import { useTrip } from "@/lib/store";
import { fmtLong } from "@/lib/dates";

export function CalendarButton({ kind, item, label = "Export to calendar", compact = false, testId }) {
  const { trip } = useTrip();
  const needsTime = kind === "event" && !item.startTime;

  const build = () => {
    if (kind === "event") return { ics: wrap([eventToV(item)]), name: item.title, g: googleUrl({ title: item.title, date: item.date, startTime: item.startTime, endTime: item.endTime, description: item.description, location: item.addressChinese || item.address || item.city }) };
    if (kind === "hotel") return { ics: wrap([hotelToV(item)]), name: `Stay ${item.name}`, g: googleUrl({ title: `Stay: ${item.name}`, date: item.checkIn, endDate: item.checkOut, description: item.address, location: item.addressChinese }) };
    if (kind === "transport") return { ics: wrap([transportToV(item)]), name: item.title, g: googleUrl({ title: item.title, date: item.date, startTime: item.departTime, endTime: item.arriveTime, description: item.carrier, location: item.toPlace || item.toCity }) };
    if (kind === "day") return { ics: dayToICS(item, trip), name: `Day ${fmtLong(item)}`, g: null };
    return { ics: tripToICS(trip), name: "Marys China Adventure", g: null };
  };

  const doDownload = () => {
    const b = build();
    download(`${b.name.replace(/[^a-z0-9]+/gi, "-")}.ics`, b.ics);
    toast.success(needsTime ? "All-day event ready — open the file to add it" : "Calendar file ready — open it to add");
  };
  const g = build().g;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={testId || `calendar-${kind}-btn`} className={`tap inline-flex items-center gap-1.5 rounded-full border font-semibold ${compact ? "px-3 py-1.5 text-xs" : "px-4 py-2.5 text-sm"} bg-white text-[var(--ink)] hover:bg-[var(--parchment-2)]`}>
          <CalendarPlus className="h-4 w-4" /> {label} <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} collisionPadding={12} className="z-[80] w-72 rounded-2xl border bg-white p-2 shadow-xl" data-testid="calendar-menu">
        <p className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--ink-muted)]">Export to personal calendar</p>
        {needsTime && <p data-testid="calendar-needs-time" className="mb-1 rounded-xl bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">Time required before this can be added as a timed event. You can add it as an all-day event instead.</p>}
        <button data-testid="calendar-download-ics" onClick={doDownload} className="tap block w-full rounded-xl px-3 py-2.5 text-left text-sm hover:bg-[var(--parchment-2)]">
          <span className="font-semibold">{needsTime ? "Add as all-day event (.ics)" : "Apple / Outlook / other (.ics)"}</span>
          <span className="block text-xs text-[var(--ink-muted)]">Works with any calendar app</span>
        </button>
        {g && (
          <a data-testid="calendar-google-link" href={g} target="_blank" rel="noreferrer" className="tap block w-full rounded-xl px-3 py-2.5 text-left text-sm hover:bg-[var(--parchment-2)]">
            <span className="font-semibold">Google Calendar</span>
            <span className="block text-xs text-[var(--ink-muted)]">Opens a pre-filled event</span>
          </a>
        )}
      </PopoverContent>
    </Popover>
  );
}
